import { BaseFeature } from '../../core/base-feature.js';

/**
 * TimelineFeature - Event timeline display and querying (Frontend)
 * Handles: querying events from desktop, displaying timeline, real-time updates
 */
export class TimelineFeature extends BaseFeature {
    constructor(coreAPI) {
        super(coreAPI);

        // Timeline state
        this.events = [];
        this.totalCount = 0;
        this.isLoading = false;
        this.hasMore = true;

        // Filters
        this.filters = {
            startDate: null,
            endDate: null,
            eventType: null
        };

        // Event listeners
        this.eventCallbacks = new Set();
        this.statsCallbacks = new Set();
        this.appUpdateCallbacks = new Set();

        // Stats cache
        this.stats = null;
        this.eventCounts = null;

        // Current app session (from AppMonitor live updates)
        this.currentAppSession = null;
    }

    static getMetadata() {
        return {
            id: 'timeline',
            name: 'Timeline',
            version: 'v1.0',
            capabilities: [
                'query_events',
                'get_event_detail',
                'get_stats',
                'receive_realtime_events'
            ],
            dependencies: []
        };
    }

    async initialize() {
        console.log('[Timeline] Initializing...');
        console.log('[Timeline] Initialized');
    }

    async handleMessage(message) {
        const { action, payload, messageId, fromId } = message;

        switch (action) {
            case 'get_events_response':
                return this.handleEventsResponse(payload);

            case 'get_event_detail_response':
                return this.handleEventDetailResponse(payload);

            case 'get_event_details_bulk_response':
                return this.handleEventDetailsBulkResponse(payload);

            case 'get_event_counts_response':
                return this.handleEventCountsResponse(payload);

            case 'get_stats_response':
                return this.handleStatsResponse(payload);

            case 'truncate_all_events_response':
                if (this._pendingTruncateResolve) {
                    if (payload.success) {
                        this.events = [];
                        this.totalCount = 0;
                        this.notifyEventListeners();
                    }
                    this._pendingTruncateResolve(payload);
                    this._pendingTruncateResolve = null;
                }
                return;

            case 'clear_old_events_response':
                if (this._pendingClearOldResolve) {
                    this._pendingClearOldResolve(payload);
                    this._pendingClearOldResolve = null;
                }
                return;

            case 'timeline_event':
                // Real-time event from desktop
                return this.handleRealtimeEvent(payload.event);

            case 'app_live_update':
                // Real-time app/title change from AppMonitor
                return this.handleAppLiveUpdate(payload);

            default:
                console.warn(`[Timeline] Unknown action: ${action}`);
        }
    }

    // =====================================
    // Event Querying
    // =====================================

    /**
     * Query events from the desktop
     * @param {Object} options - Query options
     * @param {Date|string} options.startDate - Start date filter
     * @param {Date|string} options.endDate - End date filter
     * @param {string} options.eventType - Filter by event type
     * @param {number} options.limit - Max events to return (default 500)
     * @param {number} options.offset - Offset for pagination
     * @returns {string} Message ID
     */
    queryEvents(options = {}) {
        const {
            startDate = null,
            endDate = null,
            eventType = null,
            limit = 500,
            offset = 0
        } = options;

        this.isLoading = true;

        // Convert dates to ISO strings if they're Date objects
        const payload = {
            startDate: startDate ? new Date(startDate).toISOString() : null,
            endDate: endDate ? new Date(endDate).toISOString() : null,
            eventType,
            limit,
            offset
        };

        console.log('[Timeline] Querying events:', payload);
        return this.sendMessage('get_events', payload);
    }

    /**
     * Load recent events (initial load)
     * @param {number} limit - Number of events to load
     */
    loadRecentEvents(limit = 500) {
        this.events = [];
        this.hasMore = true;
        return this.queryEvents({ limit, offset: 0 });
    }

    /**
     * Load more events (pagination)
     */
    loadMoreEvents(limit = 100) {
        if (!this.hasMore || this.isLoading) return null;
        return this.queryEvents({
            ...this.filters,
            limit,
            offset: this.events.length
        });
    }

    /**
     * Apply date filter
     * @param {Date|string} startDate - Start date
     * @param {Date|string} endDate - End date
     */
    setDateFilter(startDate, endDate) {
        this.filters.startDate = startDate;
        this.filters.endDate = endDate;
        this.events = [];
        this.hasMore = true;
        return this.queryEvents({
            startDate,
            endDate,
            eventType: this.filters.eventType,
            limit: 500,
            offset: 0
        });
    }

    /**
     * Apply event type filter
     * @param {string|null} eventType - Event type or null for all
     */
    setEventTypeFilter(eventType) {
        this.filters.eventType = eventType;
        this.events = [];
        this.hasMore = true;
        return this.queryEvents({
            ...this.filters,
            eventType,
            limit: 500,
            offset: 0
        });
    }

    /**
     * Clear all filters
     */
    clearFilters() {
        this.filters = {
            startDate: null,
            endDate: null,
            eventType: null
        };
        return this.loadRecentEvents();
    }

    handleEventsResponse(payload) {
        this.isLoading = false;

        if (!payload.success) {
            console.error('[Timeline] Failed to get events:', payload.error);
            return;
        }

        const { events, totalCount, hasMore } = payload;

        // If offset is 0, replace events; otherwise append
        if (this.events.length === 0) {
            this.events = events;
        } else {
            this.events = [...this.events, ...events];
        }

        this.totalCount = totalCount;
        this.hasMore = hasMore;

        console.log(`[Timeline] Loaded ${events.length} events (total: ${this.events.length}/${totalCount})`);

        // Notify listeners
        this.notifyEventListeners();
    }

    // =====================================
    // Event Details
    // =====================================

    /**
     * Get detailed data for an event (e.g., thumbnails)
     * @param {number} eventId - Event ID
     * @returns {string} Message ID
     */
    getEventDetail(eventId) {
        console.log('[Timeline] Getting event detail:', eventId);
        return this.sendMessage('get_event_detail', { eventId });
    }

    handleEventDetailResponse(payload) {
        if (!payload.success) {
            console.error('[Timeline] Failed to get event detail:', payload.error);
            return;
        }

        const { eventId, details } = payload;

        // Find the event and attach details
        const eventIndex = this.events.findIndex(e => e.id === eventId);
        if (eventIndex !== -1) {
            // Create new event object with details attached
            const updatedEvent = {
                ...this.events[eventIndex],
                details: details
            };

            // Create new array with the updated event (triggers React re-render)
            this.events = [
                ...this.events.slice(0, eventIndex),
                updatedEvent,
                ...this.events.slice(eventIndex + 1)
            ];

            // Notify listeners of the update
            this.notifyEventListeners();
        }
    }

    /**
     * Get detailed data for multiple events in a single request (bulk operation)
     * @param {Array<number>} eventIds - Array of event IDs
     * @returns {string} Message ID
     */
    getEventDetailsBulk(eventIds) {
        if (!eventIds || eventIds.length === 0) {
            return null;
        }
        console.log(`[Timeline] Getting details for ${eventIds.length} events in bulk`);
        return this.sendMessage('get_event_details_bulk', { eventIds });
    }

    handleEventDetailsBulkResponse(payload) {
        if (!payload.success) {
            console.error('[Timeline] Failed to get bulk event details:', payload.error);
            return;
        }

        const { detailsByEventId } = payload;
        const eventCount = Object.keys(detailsByEventId).length;
        console.log(`[Timeline] Received bulk details for ${eventCount} events`);

        // Update all events with their details
        let updated = false;
        const updatedEvents = this.events.map(event => {
            const details = detailsByEventId[event.id];
            if (details && details.length > 0) {
                updated = true;
                return { ...event, details };
            }
            return event;
        });

        if (updated) {
            this.events = updatedEvents;
            this.notifyEventListeners();
        }
    }

    // =====================================
    // Statistics
    // =====================================

    /**
     * Get event counts by type
     */
    getEventCounts() {
        return this.sendMessage('get_event_counts', {});
    }

    handleEventCountsResponse(payload) {
        if (!payload.success) {
            console.error('[Timeline] Failed to get event counts:', payload.error);
            return;
        }

        this.eventCounts = payload.counts;
        console.log('[Timeline] Event counts:', this.eventCounts);
        this.notifyStatsListeners();
    }

    /**
     * Get database statistics
     */
    getStats() {
        return this.sendMessage('get_stats', {});
    }

    handleStatsResponse(payload) {
        if (!payload.success) {
            console.error('[Timeline] Failed to get stats:', payload.error);
            return;
        }

        this.stats = payload.stats;
        console.log('[Timeline] Stats:', this.stats);
        this.notifyStatsListeners();
    }

    // =====================================
    // Database Management
    // =====================================

    /**
     * Truncate all events (delete everything)
     * @returns {Promise<Object>} Result with success and deletedCount
     */
    async truncateAllEvents() {
        console.log('[Timeline] Truncating all events...');

        return new Promise((resolve) => {
            const messageId = this.sendMessage('truncate_all_events', {});

            // Set up a one-time response handler
            const originalHandler = this.handleMessage.bind(this);
            const responseHandler = (message) => {
                if (message.action === 'truncate_all_events_response') {
                    // Clear local state
                    if (message.payload.success) {
                        this.events = [];
                        this.totalCount = 0;
                        this.notifyEventListeners();
                    }
                    resolve(message.payload);
                    return;
                }
                return originalHandler(message);
            };

            // Temporary override - will be called via handleMessage
            this._pendingTruncateResolve = resolve;
        });
    }

    /**
     * Clear old events (keep recent ones)
     * @param {number} daysToKeep - Number of days of events to keep
     * @returns {Promise<Object>} Result with success and deletedCount
     */
    async clearOldEvents(daysToKeep) {
        const olderThan = Date.now() - (daysToKeep * 24 * 60 * 60 * 1000);
        console.log(`[Timeline] Clearing events older than ${daysToKeep} days (before ${new Date(olderThan).toISOString()})`);

        return new Promise((resolve) => {
            this.sendMessage('clear_old_events', { olderThan });
            this._pendingClearOldResolve = resolve;
        });
    }

    // =====================================
    // Real-time Events
    // =====================================

    handleRealtimeEvent(event) {
        // Only add real-time events if they fall within current date filter
        if (this.filters.startDate || this.filters.endDate) {
            const eventTime = new Date(event.timestamp).getTime();
            if (this.filters.startDate && eventTime < new Date(this.filters.startDate).getTime()) return;
            if (this.filters.endDate && eventTime > new Date(this.filters.endDate).getTime()) return;
        }

        // Create new array with event at the beginning (triggers React re-render)
        this.events = [event, ...this.events];
        this.totalCount++;

        // Notify listeners
        this.notifyEventListeners();
    }

    /**
     * Handle live app updates from AppMonitor (title/app changes)
     * These provide real-time session info without creating database events
     */
    handleAppLiveUpdate(payload) {
        const { sessionInfo, changeType, oldValue, timestamp } = payload;

        // Store current app session info
        this.currentAppSession = sessionInfo;

        // Notify listeners
        this.notifyAppUpdateListeners(sessionInfo, changeType, oldValue);
    }

    // =====================================
    // Event Listeners (for React components)
    // =====================================

    /**
     * Subscribe to event updates
     * @param {Function} callback - Called when events change
     * @returns {Function} Unsubscribe function
     */
    onEventsChange(callback) {
        this.eventCallbacks.add(callback);
        return () => this.eventCallbacks.delete(callback);
    }

    /**
     * Subscribe to stats updates
     * @param {Function} callback - Called when stats change
     * @returns {Function} Unsubscribe function
     */
    onStatsChange(callback) {
        this.statsCallbacks.add(callback);
        return () => this.statsCallbacks.delete(callback);
    }

    /**
     * Subscribe to app live updates
     * @param {Function} callback - Called when app/title changes (sessionInfo, changeType, oldValue)
     * @returns {Function} Unsubscribe function
     */
    onAppUpdate(callback) {
        this.appUpdateCallbacks.add(callback);
        return () => this.appUpdateCallbacks.delete(callback);
    }

    notifyAppUpdateListeners(sessionInfo, changeType, oldValue) {
        for (const callback of this.appUpdateCallbacks) {
            try {
                callback(sessionInfo, changeType, oldValue);
            } catch (error) {
                console.error('[Timeline] Error in app update callback:', error);
            }
        }
    }

    notifyEventListeners() {
        const data = {
            events: this.events,
            totalCount: this.totalCount,
            hasMore: this.hasMore,
            isLoading: this.isLoading
        };

        for (const callback of this.eventCallbacks) {
            try {
                callback(data);
            } catch (error) {
                console.error('[Timeline] Error in event callback:', error);
            }
        }
    }

    notifyStatsListeners() {
        const data = {
            stats: this.stats,
            eventCounts: this.eventCounts
        };

        for (const callback of this.statsCallbacks) {
            try {
                callback(data);
            } catch (error) {
                console.error('[Timeline] Error in stats callback:', error);
            }
        }
    }

    // =====================================
    // Getters
    // =====================================

    getEvents() {
        return this.events;
    }

    getTotalCount() {
        return this.totalCount;
    }

    getIsLoading() {
        return this.isLoading;
    }

    getHasMore() {
        return this.hasMore;
    }

    getFilters() {
        return { ...this.filters };
    }

    getEventCounts() {
        return this.eventCounts;
    }

    getStats() {
        return this.stats;
    }

    getCurrentAppSession() {
        return this.currentAppSession;
    }

    // =====================================
    // Cleanup
    // =====================================

    async cleanup() {
        console.log('[Timeline] Cleaning up...');
        this.events = [];
        this.eventCallbacks.clear();
        this.statsCallbacks.clear();
        this.appUpdateCallbacks.clear();
        this.currentAppSession = null;
    }
}
